import { ComponentProps } from 'react';

import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isSameHour,
  isSameMinute,
  isValid,
  setDate,
  setHours,
  setMilliseconds,
  setMinutes,
  setMonth,
  setSeconds,
  setYear,
  subMilliseconds,
} from 'date-fns';

import { Button } from '../Button';

import { Modal } from './Modal';

interface DatePickerCommonProps {
  readonly startDate?: string | Date;
  readonly allDay?: boolean;
  readonly timePicker?: boolean;
  readonly dates?: ComponentProps<typeof Modal>['dates'];
  readonly size?: ComponentProps<typeof Button>['size'];
  readonly className?: string;
  readonly disabled?: boolean;
}

interface DatePickerSingleProps {
  readonly rangePicker?: false;
  readonly endDate?: never;
  readonly onChangeDate?: (startDate: Date, allDay: boolean) => void;
}

interface DatePickerRangeProps {
  readonly rangePicker: true;
  readonly endDate?: string | Date;
  readonly onChangeDate?: (startDate: Date, endDate: Date, allDay: boolean) => void;
}

type DatePickerProps = DatePickerCommonProps & (DatePickerSingleProps | DatePickerRangeProps);

const now = new Date();
const MIN_DATE = new Date(now.getFullYear() - 10, new Date().getMonth(), new Date().getDate());
const MAX_DATE = new Date(now.getFullYear() + 10, new Date().getMonth(), new Date().getDate());

function getDisplayDate(start: Date, end: Date, allDay?: boolean) {
  if (isSameDay(start, end)) {
    return `${format(start, 'd/M/yyyy')}${
      !allDay ? `, ${format(start, 'HH:mm')}${isSameHour(start, end) && isSameMinute(start, end) ? '' : ` – ${format(end, 'HH:mm')}`}` : ''
    }`;
  }

  if (allDay) {
    return `${format(start, 'd/M/yyyy')} – ${format(end, 'd/M/yyyy')}`;
  }

  return `${format(start, 'd/M/yyyy, HH:mm')} – ${format(end, 'd/M/yyyy, HH:mm')}`;
}

function clampTo(
  date: Date,
  {
    start,
    end,
  }: {
    start: Date;
    end: Date;
  },
) {
  if (isAfter(end, date) || isEqual(end, date)) {
    if (isBefore(start, date) || isEqual(start, date)) {
      return date;
    }

    // Set the date, month, year to today
    // But leave hours etc as is...
    return setDate(setMonth(setYear(date, start.getFullYear()), start.getMonth()), start.getDate());
  }

  // Set the date, month, year to today
  // But leave hours etc as is...
  return setDate(setMonth(setYear(date, end.getFullYear()), end.getMonth()), end.getDate());
}

function getValidDate(date?: Date | string, hour = new Date().getHours()) {
  // eslint-disable-next-line no-nested-ternary
  const _date = date
    ? new Date(date)
    : hour === 24
    ? endOfDay(new Date())
    : setHours(setMinutes(setSeconds(setMilliseconds(new Date(), 0), 0), 0), hour);

  if (isValid(_date)) {
    return _date;
  }

  return hour === 24 ? endOfDay(new Date()) : setHours(setMinutes(setSeconds(setMilliseconds(new Date(), 0), 0), 0), hour);
}

function getDifferences(start: Date, end: Date, minDays?: number) {
  const _start = subMilliseconds(start, 1);
  const diffInDays = differenceInDays(end, _start);
  const diffInMonths = differenceInMonths(end, _start);
  const diffInYears = differenceInYears(end, _start);

  return {
    diffInDays: minDays ? Math.max(diffInDays, minDays) : diffInDays,
    diffInMonths,
    diffInYears,
  };
}

function setDateWithoutChangingTime(oldDate: Date, newDate: Date) {
  return setDate(setMonth(setYear(oldDate, newDate.getFullYear()), newDate.getMonth()), newDate.getDate());
}

export { getDisplayDate, clampTo, getValidDate, getDifferences, setDateWithoutChangingTime, MIN_DATE, MAX_DATE };
export type { DatePickerProps };
